import { DAEMON_URL } from "../config";

/** Per-pack Automatic-QA configuration row, as returned by the daemon's admin
 *  /qa/auto endpoints (a subset of the daemon's QaAutoConfigRow). */
export interface QaAutoConfig {
  project_id: string;
  enabled: number;
  branch: string | null;
  max_runs_per_day: number;
  cooldown_seconds: number;
  consecutive_errors: number;
  runs_today: number;
  in_flight_job_id: number | null;
  paused_reason: string | null;
}
/** One historical Automatic-QA run for a pack (newest first from the daemon). */
export interface QaAutoRun {
  id: number; branch: string | null; job_id: number | null; status: string;
  verdict: string | null; findings_new: number; findings_total: number;
  cost_cents: number | null; screenshot_url: string | null; started_at: number; finished_at: number | null;
  /** Browser-reachable link to this run's qa-agent report page (/qa/<jobId>).
   *  Null when the run never got a job id (e.g. it errored before dispatch). */
  report_url: string | null;
}
/** A page of Automatic-QA runs plus the total count, for the paginated history. */
export interface QaAutoRunsPage { runs: QaAutoRun[]; total: number; limit: number; offset: number; }
export interface QaAutoResponse { config: QaAutoConfig | null; status?: { armed: boolean; pausedReason: string | null }; }

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try { const j = (await res.json()) as { error?: string }; if (j?.error) msg = j.error; } catch { /* keep */ }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

/** Read the current Automatic-QA config + arm status for a pack. Admin-only on
 *  the daemon; the dashboard cookie is the only auth the browser sends. */
export async function getQaAuto(project: string): Promise<QaAutoResponse> {
  return parse(await fetch(`${DAEMON_URL}/qa/auto?project=${encodeURIComponent(project)}`, {
    credentials: "include", headers: { Accept: "application/json" },
  }));
}

/** Patch the Automatic-QA config and/or arm/disarm the pack. The daemon records
 *  the logged-in admin's email as the actor and enforces single-armed. */
export async function setQaAuto(
  project: string,
  patch: { enabled?: boolean; branch?: string; maxRunsPerDay?: number; cooldownSeconds?: number },
): Promise<QaAutoResponse> {
  return parse(await fetch(`${DAEMON_URL}/qa/auto`, {
    method: "PUT", credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ project, ...patch }),
  }));
}

/** List a page of the Automatic-QA run history for a pack (newest first). Pass
 *  `limit`/`offset` to page through; returns the rows plus the total count so the
 *  caller can render page controls. */
export async function listQaAutoRuns(
  project: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<QaAutoRunsPage> {
  const limit = opts.limit ?? 20;
  const offset = Math.max(0, opts.offset ?? 0);
  const q = `project=${encodeURIComponent(project)}&limit=${limit}&offset=${offset}`;
  const d = await parse<Partial<QaAutoRunsPage> & { runs: QaAutoRun[] }>(
    await fetch(`${DAEMON_URL}/qa/auto/runs?${q}`, {
      credentials: "include", headers: { Accept: "application/json" },
    }),
  );
  return { runs: d.runs, total: d.total ?? d.runs.length, limit: d.limit ?? limit, offset: d.offset ?? offset };
}

/** List the branches the qa-agent can build for this pack's repo. The daemon
 *  proxies the qa-agent (token injected server-side) and soft-degrades to an
 *  empty list when the qa-agent is unset/unreachable. */
export async function listQaBranches(project: string): Promise<string[]> {
  const d = await parse<{ branches: string[] }>(await fetch(`${DAEMON_URL}/qa/branches?project=${encodeURIComponent(project)}`, {
    credentials: "include", headers: { Accept: "application/json" },
  }));
  return d.branches;
}
